import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Clock, Loader2, X } from "lucide-react";
import { usePendingWrites, type PendingStatus } from "@/store/pendingWrites";
import { cn } from "@/lib/cn";
import { useT } from "@/lib/i18n";

/**
 * F21 — pending-writes pip + popover, mounted in the Topbar.
 *
 * Idle-INVISIBLE: renders null whenever the shared store is empty, so it adds
 * no chrome on an ordinary page load. As soon as a write is marked pending (or
 * was recently resolved and hasn't auto-dropped yet) the pip appears: a small
 * count badge that opens a popover listing pending vs applied/rejected writes,
 * each with a carry-aware deep-link back to the issuing page.
 *
 * It is purely a reflection of store/pendingWrites — it never polls an echo
 * or decides applied/rejected itself; the originating handlers do that.
 */
export function PendingWritesPip() {
  const writes = usePendingWrites((s) => s.writes);
  const drop = usePendingWrites((s) => s.drop);
  const [open, setOpen] = useState(false);
  const t = useT();

  // Idle-invisible: nothing in the store → no pip at all.
  if (writes.length === 0) return null;

  const pending = writes.filter((w) => w.status === "pending");
  const resolved = writes.filter((w) => w.status !== "pending");
  // While any write is still pending the badge counts those; once everything
  // has resolved it shows the resolved count until they auto-drop.
  const count = pending.length > 0 ? pending.length : resolved.length;
  // Tone follows the worst current state: a rejection dominates, then pending,
  // else the calm applied/unconfirmed resting tone.
  const hasRejected = writes.some((w) => w.status === "rejected" || w.status === "unconfirmed");
  const tone = hasRejected
    ? "border-warn/40 bg-warn/10 text-warn"
    : pending.length > 0
      ? "border-accent/40 bg-accent/10 text-accent"
      : "border-edge bg-surface text-text-muted";

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((x) => !x)}
        className={cn(
          "grid h-8 min-w-8 place-items-center gap-1 rounded-md border px-1.5 text-[11px] font-medium",
          tone,
        )}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={`${t("topbar.pending.aria")}: ${count}`}
        title={t("topbar.pending.title")}
      >
        <span className="inline-flex items-center gap-1">
          {pending.length > 0 ? (
            <Loader2 size={12} className="animate-spin" />
          ) : (
            <Check size={12} />
          )}
          {/* Count is sr-only on the narrowest widths but always rendered so
              the pip can't overflow the topbar; the digit itself is tiny. */}
          <span className="num">{count}</span>
        </span>
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div className="absolute right-0 top-9 z-50 w-[300px] overflow-hidden rounded-md border border-edge bg-surface shadow-xl">
            <div className="border-b border-edge px-3 py-2 text-[10px] uppercase tracking-wider text-text-faint">
              {t("topbar.pending.title")}
            </div>
            <div className="max-h-[60vh] overflow-y-auto px-3 py-2">
              {pending.length > 0 && (
                <Section heading={t("topbar.pending.sectionPending")}>
                  {pending.map((w) => (
                    <Row key={w.id} kind={w.kind} label={w.label} status={w.status} href={w.href} onClose={() => setOpen(false)} />
                  ))}
                </Section>
              )}
              {resolved.length > 0 && (
                <Section heading={t("topbar.pending.sectionResolved")}>
                  {resolved.map((w) => (
                    <Row
                      key={w.id}
                      kind={w.kind}
                      label={w.label}
                      status={w.status}
                      href={w.href}
                      onClose={() => setOpen(false)}
                      onDismiss={() => drop(w.id)}
                    />
                  ))}
                </Section>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Section({ heading, children }: { heading: string; children: React.ReactNode }) {
  return (
    <div className="mb-2 last:mb-0">
      <div className="mb-1 text-[10px] uppercase tracking-wider text-text-faint">{heading}</div>
      <ul className="space-y-1">{children}</ul>
    </div>
  );
}

const STATUS_TONE: Record<PendingStatus, string> = {
  pending: "text-accent",
  applied: "text-ok",
  rejected: "text-critical",
  unconfirmed: "text-warn",
};

function StatusIcon({ status }: { status: PendingStatus }) {
  switch (status) {
    case "pending":
      return <Loader2 size={11} className="animate-spin" />;
    case "applied":
      return <Check size={11} />;
    case "rejected":
      return <X size={11} />;
    case "unconfirmed":
      return <Clock size={11} />;
  }
}

function Row({
  kind,
  label,
  status,
  href,
  onClose,
  onDismiss,
}: {
  kind: "schedule" | "override" | "alarmRule";
  label: string;
  status: PendingStatus;
  href: string;
  onClose: () => void;
  onDismiss?: () => void;
}) {
  const t = useT();
  return (
    <li className="flex items-start gap-2 rounded-md border border-edge bg-surface-2/40 px-2 py-1.5 text-[11px]">
      <span className={cn("mt-0.5 shrink-0", STATUS_TONE[status])}>
        <StatusIcon status={status} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="truncate font-semibold text-text">
          {t("topbar.pending.kind." + kind)}
          {label && <span className="font-normal text-text-muted"> · {label}</span>}
        </div>
        <div className={cn("text-[10px]", STATUS_TONE[status])}>
          {t("topbar.pending.status." + status)}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <Link
          to={href}
          onClick={onClose}
          className="rounded border border-edge bg-surface px-1.5 py-0.5 text-[10px] text-text-muted hover:text-text"
        >
          {t("topbar.pending.view")}
        </Link>
        {onDismiss && (
          <button
            onClick={onDismiss}
            className="text-text-faint hover:text-text"
            aria-label={t("common.cancel")}
          >
            <X size={11} />
          </button>
        )}
      </div>
    </li>
  );
}
